import { Slot } from '@radix-ui/react-slot';
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { flushSync } from 'react-dom';

import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';

type FormFieldContextValue = {
  id: string;
  descriptionId: string;
  errorId: string;
  describedBy: string | undefined;
  invalid: boolean;
  required: boolean;
};

const FormFieldContext = createContext<FormFieldContextValue | null>(null);

function useFormFieldContext(component: string): FormFieldContextValue {
  const ctx = useContext(FormFieldContext);
  if (!ctx) {
    throw new Error(`${component} must be used within a FormField`);
  }
  return ctx;
}

export type FormFieldProps = {
  label: ReactNode;
  error?: string | undefined;
  description?: string | undefined;
  required?: boolean | undefined;
  /**
   * - vertical: label above control (default)
   * - horizontal: control then label (checkboxes)
   * - row: label in a left column, control in the right (dialog grids)
   */
  orientation?: 'vertical' | 'horizontal' | 'row';
  className?: string;
  labelClassName?: string;
  /** Align the row label to the top (e.g. multi-line control groups). */
  alignTop?: boolean;
  children: ReactNode;
};

/**
 * Owns a generated id, wires Label htmlFor, and exposes ARIA ids via context
 * for FormControl. Pass `error` / `required` / `description`; wrap the
 * focusable control (Input, SelectTrigger, Checkbox, Textarea) in FormControl.
 */
export function FormField({
  label,
  error,
  description,
  required = false,
  orientation = 'vertical',
  className,
  labelClassName,
  alignTop = false,
  children,
}: FormFieldProps) {
  const reactId = useId();
  const id = `field-${reactId}`;
  const descriptionId = `${id}-description`;
  const errorId = `${id}-error`;
  const invalid = Boolean(error);

  const describedBy = [error ? errorId : null, description ? descriptionId : null]
    .filter((x): x is string => x !== null)
    .join(' ');

  const value: FormFieldContextValue = {
    id,
    descriptionId,
    errorId,
    describedBy: describedBy.length > 0 ? describedBy : undefined,
    invalid,
    required,
  };

  const labelEl = (
    <Label htmlFor={id} className={labelClassName}>
      {label}
      {required ? (
        <span className="text-destructive ml-0.5" aria-hidden="true">
          *
        </span>
      ) : null}
    </Label>
  );

  const descriptionEl = description ? (
    <p className="text-sm text-muted-foreground" id={descriptionId}>
      {description}
    </p>
  ) : null;

  // Stay mounted. Chrome moves focus to the next control when the node an
  // input's aria-describedby points at is removed, so clearing an error on
  // the first keystroke was jumping to the next field.
  const errorEl = (
    <p className={cn('text-sm text-destructive', !error && 'hidden')} id={errorId}>
      {error}
    </p>
  );

  if (orientation === 'horizontal') {
    return (
      <FormFieldContext.Provider value={value}>
        <div className={cn('flex items-start gap-2', className)}>
          {children}
          <div className="flex flex-col gap-1.5">
            {labelEl}
            {descriptionEl}
            {errorEl}
          </div>
        </div>
      </FormFieldContext.Provider>
    );
  }

  if (orientation === 'row') {
    return (
      <FormFieldContext.Provider value={value}>
        <div
          className={cn(
            'grid grid-cols-4 gap-4',
            alignTop ? 'items-start' : 'items-center',
            className
          )}
        >
          <Label htmlFor={id} className={cn('text-right', alignTop && 'pt-2', labelClassName)}>
            {label}
            {required ? (
              <span className="text-destructive ml-0.5" aria-hidden="true">
                *
              </span>
            ) : null}
          </Label>
          <div className="col-span-3 flex flex-col gap-1.5">
            {descriptionEl}
            {children}
            {errorEl}
          </div>
        </div>
      </FormFieldContext.Provider>
    );
  }

  return (
    <FormFieldContext.Provider value={value}>
      <div className={cn('flex flex-col gap-1.5', className)}>
        {labelEl}
        {descriptionEl}
        {children}
        {errorEl}
      </div>
    </FormFieldContext.Provider>
  );
}

export type FormControlProps = {
  children: ReactNode;
};

/**
 * Merges id, aria-invalid, aria-describedby, and aria-required onto its single
 * child. Wrap the focusable element (SelectTrigger, not Select).
 */
export function FormControl({ children }: FormControlProps) {
  const { id, describedBy, invalid, required } = useFormFieldContext('FormControl');

  return (
    <Slot
      id={id}
      aria-invalid={invalid || undefined}
      aria-describedby={describedBy}
      aria-required={required || undefined}
    >
      {children}
    </Slot>
  );
}

export type FormErrorItem = {
  label: string;
  message: string;
};

export type FormErrorSummaryProps = {
  errors: FormErrorItem[];
  /** Bump after each failed submit; announcement and focus run only when this changes. */
  attempt: number;
  /** Runs once per attempt after the announcement is written, e.g. to focus the first invalid field. */
  onAnnounced?: () => void;
  title?: string;
  className?: string;
};

// Screen readers drop an assertive announcement that is still queued when
// focus moves, so focus waits until the summary has been written.
const ANNOUNCE_BEFORE_FOCUS_MS = 150;

/**
 * Lists "{label}: {how to fix}" and announces it through a live region that
 * stays mounted for the life of the form. Each new attempt clears and rewrites
 * the region, then calls `onAnnounced`.
 */
export function FormErrorSummary({
  errors,
  attempt,
  onAnnounced,
  title = 'Please fix the following:',
  className,
}: FormErrorSummaryProps) {
  const [announcement, setAnnouncement] = useState('');
  const latest = useRef({ errors, title, onAnnounced });
  latest.current = { errors, title, onAnnounced };

  useEffect(() => {
    setAnnouncement('');
    if (attempt === 0) return;

    // Someone who starts typing or clicking before focus moves keeps their
    // place. Only needed until the focus timer resolves, so it is removed as
    // soon as that happens rather than lingering for the rest of the attempt.
    let userTookOver = false;
    const takeOver = () => {
      userTookOver = true;
    };
    const stopWatching = () => {
      document.removeEventListener('keydown', takeOver, true);
      document.removeEventListener('pointerdown', takeOver, true);
    };
    document.addEventListener('keydown', takeOver, true);
    document.addEventListener('pointerdown', takeOver, true);

    let focusTimer: ReturnType<typeof setTimeout> | undefined;
    const announceTimer = setTimeout(() => {
      const current = latest.current;
      if (current.errors.length > 0) {
        const lines = current.errors.map((e) => `${e.label}: ${e.message}`).join('. ');
        flushSync(() => setAnnouncement(`${current.title} ${lines}`));
      }
      focusTimer = setTimeout(() => {
        if (!userTookOver) latest.current.onAnnounced?.();
        stopWatching();
      }, ANNOUNCE_BEFORE_FOCUS_MS);
    }, 0);

    return () => {
      clearTimeout(announceTimer);
      if (focusTimer) clearTimeout(focusTimer);
      stopWatching();
    };
  }, [attempt]);

  const showSummary = attempt > 0 && errors.length > 0;

  return (
    <>
      {showSummary ? (
        <div
          className={cn('mb-4 p-3 bg-red-50 border border-red-200 rounded-md shrink-0', className)}
        >
          <p className="text-sm font-medium text-red-800 mb-1">{title}</p>
          <ul className="list-disc list-inside space-y-0.5">
            {errors.map((e) => (
              <li key={`${e.label}-${e.message}`} className="text-sm text-red-700">
                <span className="font-medium">{e.label}:</span> {e.message}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      <div className="sr-only" aria-live="assertive" aria-atomic="true">
        {announcement}
      </div>
    </>
  );
}

function canReceiveFocus(el: HTMLElement): boolean {
  if (el.matches(':disabled') || el.getAttribute('aria-disabled') === 'true') return false;
  return el.tabIndex >= 0;
}

/**
 * Focus the first focusable control marked aria-invalid inside the container.
 * Disabled and non-tabbable invalid controls are skipped.
 */
export function focusFirstInvalid(container: HTMLElement | null): void {
  if (!container) return;
  const candidates = container.querySelectorAll<HTMLElement>('[aria-invalid="true"]');
  for (const el of candidates) {
    if (!canReceiveFocus(el)) continue;
    if (document.activeElement !== el) el.focus();
    return;
  }
}
